import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import axios from 'axios';
import { authAPI } from '../api/endpoints';
import { setAccessToken, clearAccessToken } from '../api/client';
import { getBrowserTimezone } from '../utils/timezone';
import type { User } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  handleAuthCallback: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const checkAuthStarted = useRef(false);
  const timezoneSeeded = useRef(false);

  const checkAuth = useCallback(async () => {
    // No in-memory token on page load — try refreshing via httpOnly cookie
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      setAccessToken(data.access_token);
    } catch {
      // No valid refresh token — user is not authenticated
      setUser(null);
      setIsLoading(false);
      return;
    }
    try {
      const meResponse = await authAPI.me();
      setUser(meResponse.data);
    } catch {
      // Refresh succeeded but /auth/me failed — clear the access token
      clearAccessToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip checkAuth on the OAuth callback page — it needs the httpOnly cookie
    // and checkAuth would consume it first (race condition).
    if (window.location.pathname === '/login/callback') {
      setIsLoading(false);
      return;
    }
    // Guard: React StrictMode fires effects twice in development. Two concurrent
    // /auth/refresh calls with the same cookie trigger reuse-detection on the
    // backend, which revokes all tokens and logs the user out on every refresh.
    if (checkAuthStarted.current) return;
    checkAuthStarted.current = true;
    checkAuth();
  }, [checkAuth]);

  // Seed the profile timezone from the browser the first time we see a user without one.
  // Batch email send windows are expressed in it, so it has to be set before the first send;
  // afterwards it is the user's to change in Profile -> Settings and we never overwrite it.
  useEffect(() => {
    if (!user || user.settings?.timezone || timezoneSeeded.current) return;
    timezoneSeeded.current = true;
    authAPI
      .updateSettings({ timezone: getBrowserTimezone() })
      .then((res) => setUser(res.data))
      .catch(() => {
        // Non-blocking: pickers fall back to the browser timezone until this succeeds
        timezoneSeeded.current = false;
      });
  }, [user]);

  const login = async () => {
    try {
      const response = await authAPI.login();
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to initiate login:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {
      // Continue with local logout even if server call fails
    }
    clearAccessToken();
    setUser(null);
    window.location.href = '/login';
  };

  // Re-fetch the authenticated user's full profile (e.g. after connecting Gmail).
  const refreshUser = useCallback(async () => {
    const meResponse = await authAPI.me();
    setUser(meResponse.data);
  }, []);

  const handleAuthCallback = useCallback(async () => {
    // Exchange the httpOnly auth_access_token cookie for the access token
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/exchange-token`,
        {},
        { withCredentials: true }
      );
      setAccessToken(data.access_token);
      // Fetch the full profile so fields like gmail_permissions_granted are
      // accurate (the exchange response only carries email/tier).
      await refreshUser();
    } catch {
      // Exchange failed — redirect to login
      setUser(null);
    }
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        handleAuthCallback,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
